<template>
  <div class="partner-tab-sharing-level-cell">
    <BittsPopover
      v-if="variant && hasPopover"
      v-model:open="isPopoverOpen"
      trigger="hover"
      placement="bottomLeft"
      :mount-to-body="true"
      :mouse-enter-delay="0.1"
      overlay-class="partner-tab-sharing-level-cell__popover"
      class="partner-tab-sharing-level-cell__trigger"
    >
      <button
        ref="cellButton"
        type="button"
        :aria-label="ariaLabel"
        class="partner-tab-sharing-level-cell__button"
        @click="openPartnerSettingsFromCell"
        @focus="openPopoverOnKeyboardFocus"
        @blur="isPopoverOpen = false"
      >
        <PartnerTabSharingLevelPill :variant="variant" />
        <BittsCount
          v-if="overridesCount"
          type="neutral"
          :count="overridesCount"
          class="partner-tab-sharing-level-cell__overrides"
          data-testid="sharing-level-overrides-count"
        />
      </button>
      <template #content>
        <PartnerTabSharingLevelPopover
          :populations="segmentPopulations"
          :side="params.side"
          :partner-org-id="params.data!.id"
          :partner-name="params.data!.partner.name"
          @open-partner-settings="openPartnerSettingsFromPopover"
        />
      </template>
    </BittsPopover>
    <PartnerTabSharingLevelPill v-else-if="variant" :variant="variant" />
    <span v-else class="text-sm text-neutral-text-placeholder">&ndash;</span>
  </div>
</template>

<script setup lang="ts">
import { BittsCount, BittsPopover } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { watchDebounced } from '@vueuse/core';
import { pluralize } from 'humanize-plus';
import { computed, ref, useTemplateRef } from 'vue';

import { useForwardCellFocus } from '@/composables/useForwardCellFocus';
import { SEGMENTS_TO_LABEL_MAP } from '@/types/populations';

import {
  SHARING_LEVEL_VARIANT,
  SHARING_LEVEL_VARIANT_LABEL,
} from '../sharingLevelVariantLabels';
import type {
  OutgoingPopulationSharingLevel,
  PopulationSharingLevel,
  SharingLevelCellParamsType,
  SharingLevelVariantType,
  SharingPartnerRow,
  SharingSideType,
} from '../types';

import PartnerTabSharingLevelPill from './PartnerTabSharingLevelPill.vue';
import PartnerTabSharingLevelPopover from './PartnerTabSharingLevelPopover.vue';

const { params } = defineProps<{
  params: ICellRendererParams<SharingPartnerRow> & SharingLevelCellParamsType;
}>();

const POPULATIONS_KEY: Record<
  SharingSideType,
  'incomingPopulations' | 'outgoingPopulations'
> = {
  incoming: 'incomingPopulations',
  outgoing: 'outgoingPopulations',
};

/** Request and Pending get their own hover behaviour in PGA-537. */
const VARIANTS_WITH_POPOVER = new Set<SharingLevelVariantType>([
  'all',
  'overlaps',
  'counts',
  'hidden',
]);

const variant = computed<SharingLevelVariantType | null>(() => {
  const level = params.data?.[params.side][params.segmentKey];
  return level ? SHARING_LEVEL_VARIANT[level] : null;
});

const segmentPopulations = computed<
  (PopulationSharingLevel | OutgoingPopulationSharingLevel)[]
>(() =>
  Object.values(params.data?.[POPULATIONS_KEY[params.side]] ?? {}).filter(
    (population) => population.segment === params.segmentKey,
  ),
);

const overridesCount = computed(() =>
  segmentPopulations.value.reduce(
    (count, population) =>
      count + ('overrides' in population ? population.overrides.length : 0),
    0,
  ),
);

const SIDE_LABEL: Record<SharingSideType, string> = {
  incoming: 'shared by',
  outgoing: 'shared with',
};

const isPopoverOpen = ref(false);

/** A popover that closes within the delay was passed over, not read. */
watchDebounced(
  isPopoverOpen,
  (isOpen) => {
    if (isOpen)
      params.context?.trackSharingPopoverViewed?.(
        params.side,
        params.segmentKey,
        variant.value,
      );
  },
  { debounce: 500 },
);

useForwardCellFocus(params.eGridCell);

const overridesLabel = computed(() =>
  overridesCount.value
    ? `, ${overridesCount.value} ${pluralize(overridesCount.value, 'override')}`
    : '',
);

const ariaLabel = computed(() =>
  variant.value
    ? `${SEGMENTS_TO_LABEL_MAP[params.segmentKey]} ${SIDE_LABEL[params.side]} ${params.data?.partner.name}: ${SHARING_LEVEL_VARIANT_LABEL[variant.value]}${overridesLabel.value}. Open sharing settings`
    : undefined,
);

/**
 * The partner settings modal hands focus back to this button when it closes.
 * After a mouse click that would reopen the popover away from the pointer, so
 * only keyboard focus, which the browser marks focus-visible, opens it.
 */
function openPopoverOnKeyboardFocus(event: FocusEvent) {
  if ((event.target as HTMLElement).matches(':focus-visible')) {
    isPopoverOpen.value = true;
  }
}

type OpenedFromType =
  | { entryPoint: 'cell'; sharingLevel: SharingLevelVariantType | null }
  | {
      entryPoint: 'popover_row' | 'popover_override';
      sharingLevel: SharingLevelVariantType;
      populationId: number;
    };

function openPartnerSettings(
  openedFrom: OpenedFromType,
  openedOverridesCount: number,
) {
  if (!params.data) return;
  isPopoverOpen.value = false;
  params.context?.trackPartnerSettingsOpened?.({
    tab: 'by_partners',
    partnerOrgId: params.data.id,
    segment: params.segmentKey,
    side: params.side,
    ...openedFrom,
    ...(params.side === 'outgoing' && { overridesCount: openedOverridesCount }),
  });
  params.context?.openPartnerSettings?.(
    params.data.id,
    params.side,
    params.segmentKey,
    openedOverridesCount > 0,
  );
}

function openPartnerSettingsFromCell() {
  openPartnerSettings(
    { entryPoint: 'cell', sharingLevel: variant.value },
    overridesCount.value,
  );
}

const cellButton = useTemplateRef<HTMLButtonElement>('cellButton');

/**
 * The modal hands focus back to whatever held it when it opened. The row sits
 * in the popover, which closes, so the cell takes focus first.
 */
function openPartnerSettingsFromPopover(
  population: PopulationSharingLevel | OutgoingPopulationSharingLevel,
  entryPoint: 'popover_row' | 'popover_override',
) {
  cellButton.value?.focus();
  openPartnerSettings(
    {
      entryPoint,
      sharingLevel: SHARING_LEVEL_VARIANT[population.level],
      populationId: population.id,
    },
    'overrides' in population ? population.overrides.length : 0,
  );
}

const hasPopover = computed(
  () =>
    !!variant.value &&
    VARIANTS_WITH_POPOVER.has(variant.value) &&
    !!segmentPopulations.value.length,
);
</script>

<style lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-sharing-level-cell {
  @apply flex items-center justify-center h-full w-full py-6;
}
.partner-tab-sharing-level-cell__trigger {
  @apply h-full w-full;
}
.partner-tab-sharing-level-cell__button {
  @apply relative h-full w-full rounded-4 cursor-pointer;
}
.partner-tab-sharing-level-cell__overrides {
  @apply absolute top-0 right-0;
}
.partner-tab-sharing-level-cell__popover.ant-popover {
  @apply w-[300px];

  .ant-popover-inner {
    @apply shadow-overlay;
  }
}
</style>
